import React from 'react';
import dayjs from 'dayjs';
import ColorPicker from './ColorPicker';
import TextAreaCustom from './TextAreaCustom';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { GridColDef } from '@mui/x-data-grid';
import { FieldErrors } from '../domain/helpers/Errors';
import { useLanguageStore } from '../store/useLanguageStore';
import { useDarkModeStore } from '../store/useDarkModeStore';
import { CustomerInterface } from '../models/TicketModule/Customer';
import { darkModeBackgroud, darkModeBackgroudInputs } from '../utils/constants';

interface TextFieldCustomProps<T> {
    form: T;
    view: boolean;
    column: GridColDef;
    errors: FieldErrors;
    setForm: React.Dispatch<React.SetStateAction<T>>;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isDate?: boolean) => void;
}
export const TextFieldCustom = <T,>({ column, form, errors, view, handleChange, setForm }: TextFieldCustomProps<T>) => {

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { field, type, headerName } = column;

    const fieldError = errors?.[field as keyof FieldErrors];
    const isBadgeColor = field === 'badgeColor';
    const isBooleanValue = type === 'boolean';
    const isDateValue = type === 'date';
    const multitext = type === 'custom';

    const handleColorPicker = (name: string, value: string) => setForm({ ...form, [name]: value });

    const value = form[field as keyof T];

    const isValidDateValue = (val: unknown): val is string | number | Date => {
        return typeof val === 'string' || typeof val === 'number' || val instanceof Date;
    };

    const labelTitle = language[headerName ?? ''] ?? headerName;

    return multitext
        ? <div className='position-relative mt-2 mb-2'>
            <label
                htmlFor={headerName}
                className='position-absolute ps-2 pe-2'
                style={{
                    top: -8,
                    left: 10,
                    zIndex: 8,
                    fontSize: 12,
                    color: darkMode ? 'white' : 'initial',
                    background: darkMode ? darkModeBackgroudInputs : 'white',
                }}
            >
                {labelTitle}
            </label>
            <TextAreaCustom
                view={view}
                name={field}
                errors={errors}
                form={form as CustomerInterface}
                className={` ${fieldError && 'border-danger'}`}
                setForm={setForm as React.Dispatch<React.SetStateAction<CustomerInterface>>}
            />
            {
                fieldError
                && <p
                    className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained css-1wc848c-MuiFormHelperText-root"
                    style={{ color: 'red', marginTop: '0.5rem' }}
                >
                    {fieldError}
                </p>
            }
        </div>
        : isBooleanValue ?
            <div className='mt-2'>
                <FormGroup className='ms-2'>
                    <FormControlLabel
                        className={`${darkMode && 'text-white'}`}
                        control={
                            <Checkbox
                                id={field}
                                checked={Boolean(form[field as keyof T]) ?? false}
                                onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
                                disabled={view}
                                sx={{
                                    color: view ? 'white' : 'inherit',
                                    '&.Mui-disabled': { color: 'grey' }
                                }}
                            />}
                        label={language[field] || field}
                        sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: darkMode ? 'white' : 'initial' } }}
                    />
                </FormGroup>
            </div>
            : isBadgeColor
                ? <ColorPicker
                    view={view}
                    name={field}
                    color={(form as { badgeColor: string }).badgeColor}
                    handleColorPicker={handleColorPicker}
                />
                : isDateValue
                    ? <TextField
                        key={field}
                        id={field}
                        type={type}
                        variant="outlined"
                        label={headerName}
                        className='mt-2 mb-2'
                        value={isDateValue && isValidDateValue(value) ? dayjs(value).format('YYYY-MM-DD') : value || ''}
                        onChange={(e) => handleChange(e, type === 'date')}
                        error={Boolean(fieldError)}
                        helperText={fieldError || ''}
                        style={darkMode ? { background: darkModeBackgroud, color: 'white' } : {}}
                        InputProps={{
                            readOnly: view,
                            style: {
                                color: darkMode ? 'white' : 'black',
                            }
                        }}
                        InputLabelProps={{
                            shrink: isDateValue ? true : false,
                            style: {
                                color: darkMode ? 'white' : '',
                            }
                        }}
                    />
                    : <TextField
                        key={field}
                        id={field}
                        type={type}
                        label={headerName}
                        variant="outlined"
                        className='mt-2 mb-2'
                        value={value || ''}
                        onChange={(e) => handleChange(e)}
                        error={Boolean(fieldError)}
                        helperText={fieldError || ''}
                        style={darkMode ? { background: darkModeBackgroud, color: 'white' } : {}}
                        InputProps={{
                            readOnly: view,
                            style: { color: darkMode ? 'white' : 'black' }
                        }}
                        InputLabelProps={{
                            style: { color: darkMode ? 'white' : '' }
                        }}
                    />
}
export default TextFieldCustom